import React, { useEffect, useState } from "react";
import FormBox from "../../components/FormBox/FormBox";
import { LoadingButton, SpinnerBlock } from "../../Utils/CustomComponent";
import { useForm } from "react-hook-form";
import axios from "../../axios";
import urls from "../../Utils/urls";
import { useAbortController } from "../../hooks/useAbortController";
import { profileDetailsSchema } from "../../Utils/formOptions";
import { InfoCircleFill } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { commonToast } from "../../Utils";
import { oracleConfig } from "../../Utils/constant";
import SmsOptInModal, {
  SMS_CONSENT_VERSION,
  SMS_OPT_IN_TEXT,
  SMS_OPT_OUT_TEXT,
} from "./SmsOptInModal";

const UserDetailsProfile = () => {
  const [disableBtn, setDisableBtn] = useState(false);
  const [loading, setLoading] = useState(false);
  // true/false = choice awaiting confirmation in the popup, null = popup closed
  const [pendingSmsOptIn, setPendingSmsOptIn] = useState(null);
  const [smsOptInSaving, setSmsOptInSaving] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({ ...profileDetailsSchema });
  const { signal } = useAbortController();

  const unsubscribe = watch("subscribe_yn");
  const userId = localStorage.getItem("apex_userid");

  // The opt-in checkbox only opens the confirmation popup; its value changes after the API succeeds
  const setFormValue = (field, value, options) => {
    if (field !== "sms_opt_in") {
      setValue(field, value, options);
      return;
    }
    const optIn = Boolean(value);
    if (optIn && !String(getValues("mobile") || "").replace(/\D/g, "")) {
      toast.error("Please enter your mobile number before opting in to SMS OTP.", {
        ...commonToast,
      });
      return;
    }
    setPendingSmsOptIn(optIn);
  };

  const applySmsOpt = (smsOpt) => {
    setValue("sms_opt_in", String(smsOpt || "").toUpperCase() === "Y");
  };

  // Prefer sms_opt_in; keep sms_opt as a fallback while Oracle is renaming the field
  const readOracleSmsOpt = (item) => item?.sms_opt_in ?? item?.sms_opt ?? null;

  async function fetchSmsOptStatus() {
    const res = await axios.get(urls.borrower.profileDetails(userId), oracleConfig);
    return readOracleSmsOpt(res.data?.items?.[0]);
  }

  async function confirmSmsOptIn() {
    const optIn = pendingSmsOptIn;
    const data = getValues();
    setSmsOptInSaving(true);
    try {
      await axios.post(
        urls.borrower.updateSmsOptIn,
        {
          user_id: String(userId),
          sms_opt_in: optIn ? "Y" : "N",
          mobile: data.mobile,
          email: data.email,
          username: data.username,
          first_name: data.first_name,
          last_name: data.last_name,
          consent_text: optIn ? SMS_OPT_IN_TEXT : SMS_OPT_OUT_TEXT,
          consent_version: SMS_CONSENT_VERSION,
          source: "profile",
        },
        oracleConfig
      );

      applySmsOpt(optIn ? "Y" : "N");
      setPendingSmsOptIn(null);
      toast.success(
        optIn
          ? "You have opted in to receive login OTP via SMS."
          : "You have opted out of receiving login OTP via SMS.",
        { ...commonToast }
      );

      fetchSmsOptStatus()
        .then((smsOpt) => {
          if (smsOpt === "Y" || smsOpt === "N") applySmsOpt(smsOpt);
        })
        .catch((error) => console.error(error));
    } catch (error) {
      console.error(error);
      toast.error("SMS OTP preference could not be saved. Please try again.", {
        ...commonToast,
      });
      setPendingSmsOptIn(null);
    } finally {
      setSmsOptInSaving(false);
    }
  }

  useEffect(() => {
    async function getDetails() {
      setLoading(true);
      try {
        const res = await axios.get(
          urls.borrower.profileDetails(userId),
          oracleConfig,
          { signal }
        );

        const data = res.data.items?.[0] || {};
        applySmsOpt(readOracleSmsOpt(data));
        Object.entries(data).forEach(([key, value]) => {
          if (key === "sms_opt_in" || key === "sms_opt") {
            return;
          }
          if (value && key !== "subscribe_yn") {
            setValue(key, value);
          } else if (key === "subscribe_yn") {
            const updatedVal = value === "1" ? null : true;
            setValue(key, updatedVal);
          }
        });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }

    getDetails();
  }, []);

  async function submitProfile(data) {
    setDisableBtn(true);
    // SMS opt-in is saved separately through its own popup/endpoint
    const { sms_opt_in, sms_opt, ...profileData } = data;
    try {
      const res = await axios.post(
        urls.borrower.profileDetails(userId),
        profileData,
        oracleConfig
      );
      toast.success("Profile details updated successfully!", {
        ...commonToast,
      });
    } catch (error) {
      console.error(error);
      toast.error("Profile updation failed!", {
        ...commonToast,
      });
    } finally {
      setDisableBtn(false);
    }
  }

  return !loading ? (
    <div className="border border-2 p-3 pb-5">
      <FormBox
        options={profileArr}
        register={register}
        watch={watch}
        setValue={setFormValue}
        errors={errors}
      />
      <SmsOptInModal
        pendingOptIn={pendingSmsOptIn}
        loading={smsOptInSaving}
        onConfirm={confirmSmsOptIn}
        onCancel={() => !smsOptInSaving && setPendingSmsOptIn(null)}
      />
      {unsubscribe ? (
        <div
          className="alert alert-info p-5 mx-3 border d-flex gap-5"
          role="alert"
        >
          <div className="">
            <InfoCircleFill size={24} />
          </div>
          <div className="">
            You are unsubscribing from updates for the support ticket(s) which
            you have open. You can view updates on support ticket(s) on support
            portal{" "}
            <a
              href="https://helpdesk.loanlinq.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-underline"
            >
              helpdesk.loanlinq.com
            </a>
            .
          </div>
        </div>
      ) : null}
      <div className="mt-5">
        <LoadingButton
          type="button"
          className="btn btn-primary mt-3 text-white"
          onClick={handleSubmit(submitProfile)}
          title={"Submit"}
          disabled={disableBtn}
          loading={disableBtn}
        />
      </div>
    </div>
  ) : (
    <div>
      <SpinnerBlock height="50vh" />
    </div>
  );
};

export default UserDetailsProfile;

const profileArr = [
  {
    content: "*Username",
    type: "content",
    className: "col-2 fw-bold fs-5 d-flex align-self-center",
  },
  {
    title: "Username",
    field: "username",
    type: "text",
    className: "col-10",
    disable: true,
    blur: true,
  },
  {
    content: "First Name",
    type: "content",
    className: "col-2 fw-bold fs-5 d-flex align-self-center",
  },
  {
    title: "First Name",
    field: "first_name",
    type: "text",
    className: "col-10",

    blur: true,
  },
  {
    content: "Last Name",
    type: "content",
    className: "col-2 fw-bold fs-5 d-flex align-self-center",
  },
  {
    title: "Last Name",
    field: "last_name",
    type: "text",
    className: "col-10",

    blur: true,
  },
  {
    content: "Email",
    type: "content",
    className: "col-2 fw-bold fs-5 d-flex align-self-center",
  },
  {
    title: "Email",
    field: "email",
    type: "email",
    className: "col-10",
    disable: true,
    blur: true,
  },
  {
    content: "Mobile",
    type: "content",
    className: "col-2 fw-bold fs-5 d-flex align-self-center",
  },
  {
    title: "Mobile",
    field: "mobile",
    type: "phoneNumber",
    className: "col-6",
    blur: true,
  },
  {
    title: "Opt in to receive login OTP via SMS",
    field: "sms_opt_in",
    type: "checkbox",
    className: "col-4 fw-bold fs-6 d-flex align-items-center",
    blur: true,
  },
  {
    title: "Unsubscribe from support emails",
    field: "subscribe_yn",
    type: "checkbox",
    className: "col-12 fw-bold fs-5",
    blur: true,
  },
];
